import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Upload, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { createEmail } from '../../../services/emailServiceSwitch';
import { parseEmailCsv } from '../../../utils/csvHelpers';
import './EmailManagement.css';

/**
 * ImportEmailsModal Component
 *
 * Parses a CSV file client-side, previews rows, then calls createEmail for each.
 * Handles duplicates gracefully (DUPLICATE_EMAIL error = skip).
 * Shows progress bar and final summary.
 */
export default function ImportEmailsModal({ isOpen, onClose, onSuccess }) {
  const fileInputRef = useRef(null);

  // Steps: 'upload' | 'preview' | 'importing' | 'done'
  const [step, setStep] = useState('upload');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ imported: 0, skipped: 0, failed: 0, errors: [] });

  const reset = () => {
    setStep('upload');
    setParsedRows([]);
    setParseErrors([]);
    setProgress({ current: 0, total: 0 });
    setResults({ imported: 0, skipped: 0, failed: 0, errors: [] });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (step === 'importing') return; // don't close mid-import
    reset();
    onClose();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const { data, errors } = parseEmailCsv(evt.target.result);
      setParsedRows(data);
      setParseErrors(errors);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setStep('importing');
    const total = parsedRows.length;
    setProgress({ current: 0, total });

    const res = { imported: 0, skipped: 0, failed: 0, errors: [] };

    for (let i = 0; i < total; i++) {
      const row = parsedRows[i];
      try {
        await createEmail({ email: row.email, name: row.name });
        res.imported++;
      } catch (err) {
        const errData = err?.response?.data?.error || err?.response?.data || {};
        const code = errData.code || '';
        const msg = errData.message || err.message || 'Unknown error';
        if (code === 'DUPLICATE_EMAIL' || msg.includes('already exists')) {
          res.skipped++;
        } else {
          res.failed++;
          res.errors.push(`Row ${i + 1}: ${msg}`);
        }
      }
      setProgress({ current: i + 1, total });
    }

    setResults(res);
    setStep('done');
  };

  const handleDone = () => {
    const msg = `Import complete: ${results.imported} imported, ${results.skipped} already existed, ${results.failed} failed`;
    if (onSuccess) onSuccess(msg);
    reset();
    onClose();
  };

  // --- Render helpers ---

  const renderUpload = () => (
    <div className="import-upload-area">
      <div className="upload-icon"><Upload size={40} /></div>
      <p className="upload-text">Select a CSV file with columns: <code>email</code>, <code>name</code></p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileSelect}
        className="file-input"
        aria-label="Select CSV file"
      />
      <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
        Choose File
      </Button>
    </div>
  );

  const renderPreview = () => (
    <div className="import-preview">
      {parseErrors.length > 0 && (
        <div className="import-warnings">
          <AlertTriangle size={16} />
          <div>
            {parseErrors.map((e, i) => <div key={i} className="warning-line">{e}</div>)}
          </div>
        </div>
      )}
      <p className="preview-count">{parsedRows.length} valid email(s) found</p>
      <div className="preview-table-wrap">
        <table className="preview-table">
          <thead><tr><th>Email</th><th>Name</th></tr></thead>
          <tbody>
            {parsedRows.slice(0, 50).map((r, i) => (
              <tr key={i}><td>{r.email}</td><td>{r.name || '—'}</td></tr>
            ))}
            {parsedRows.length > 50 && (
              <tr><td colSpan={2} className="more-rows">…and {parsedRows.length - 50} more</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderImporting = () => {
    const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
    return (
      <div className="import-progress">
        <p>Importing {progress.current} of {progress.total}…</p>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="progress-pct">{pct}%</p>
      </div>
    );
  };

  const renderDone = () => (
    <div className="import-results">
      <div className="result-row result-success"><CheckCircle size={18} /> {results.imported} imported</div>
      <div className="result-row result-skipped"><AlertTriangle size={18} /> {results.skipped} already existed (skipped)</div>
      {results.failed > 0 && (
        <div className="result-row result-failed"><XCircle size={18} /> {results.failed} failed</div>
      )}
      {results.errors.length > 0 && (
        <div className="import-error-list">
          {results.errors.slice(0, 10).map((e, i) => <div key={i} className="error-line">{e}</div>)}
        </div>
      )}
    </div>
  );

  const footerByStep = {
    upload: null,
    preview: (
      <div className="modal-actions">
        <Button variant="secondary" onClick={reset}>Back</Button>
        <Button variant="primary" onClick={handleImport} disabled={parsedRows.length === 0} icon={<Upload size={18} />}>
          Import {parsedRows.length} Email{parsedRows.length !== 1 ? 's' : ''}
        </Button>
      </div>
    ),
    importing: null,
    done: (
      <div className="modal-actions">
        <Button variant="primary" onClick={handleDone}>Done</Button>
      </div>
    )
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Emails from CSV"
      size="md"
      closeOnBackdrop={step !== 'importing'}
      footer={footerByStep[step]}
    >
      {step === 'upload' && renderUpload()}
      {step === 'preview' && renderPreview()}
      {step === 'importing' && renderImporting()}
      {step === 'done' && renderDone()}
    </Modal>
  );
}

ImportEmailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
